// src/components/payroll/PayrollViewModal.js
import React from 'react';
import { X } from 'lucide-react';

const PayrollViewModal = ({ entry, isOpen, onClose }) => {
  if (!isOpen || !entry) return null;

  // Defensive checks to prevent errors if nested properties are missing
  const payroll = entry || {};
  const staff = payroll.staff || {};
  const payPeriod = payroll.payPeriod || {};
  const salary = payroll.salary || {};
  const approval = payroll.approval || {};

  const paymentDetails = [
    { label: "Basic Salary", value: salary.basic },
    { label: "Total Allowances", value: payroll.totalAllowances },
    { label: "Total Bonuses", value: payroll.totalBonuses },
    { label: "Arrears", value: payroll.arrears },
    { label: "Total Deductions", value: payroll.totalDeductions },
  ];

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 my-8 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-800">Payroll Details for {staff.firstName} {staff.lastName}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 transition">
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {/* Employee Information Section */}
          <section className="mb-6">
            <h4 className="text-lg font-semibold text-orange-600 mb-2">Employee Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{staff.firstName} {staff.lastName}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Department</p>
                <p className="font-medium text-gray-900">{staff.department || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Job Title</p>
                <p className="font-medium text-gray-900">{staff.position || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{staff.email || 'N/A'}</p>
              </div>
            </div>
          </section>

          {/* Pay Period & Approval Section */}
          <section className="mb-6">
            <h4 className="text-lg font-semibold text-orange-600 mb-2">Pay Period & Status</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Month / Year</p>
                <p className="font-medium text-gray-900">{payroll.month} / {payPeriod.year}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Pay Period</p>
                <p className="font-medium text-gray-900">{formatDate(payPeriod.startDate)} - {formatDate(payPeriod.endDate)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Payment Method</p>
                <p className="font-medium text-gray-900 capitalize">{payroll.paymentMethod?.replace(/_/g, ' ') || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-gray-500">Approval Status</p>
                <p className="font-medium text-gray-900 capitalize">{approval.status?.replace(/_/g, ' ') || 'N/A'}</p>
              </div>
            </div>
          </section>

          {/* Financial Breakdown Section */}
          <section className="mb-6">
            <h4 className="text-lg font-semibold text-orange-600 mb-2">Financial Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {paymentDetails.map((item, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-md">
                  <p className="text-gray-500">{item.label}</p>
                  <p className="font-medium text-gray-900">{formatCurrency(item.value)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Summary Section */}
          <section>
            <h4 className="text-lg font-semibold text-orange-600 mb-2">Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-green-50 p-4 rounded-md">
                <p className="text-gray-500 font-bold">Gross Pay</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(payroll.grossPay)}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-md">
                <p className="text-gray-500 font-bold">Net Pay</p>
                <p className="text-2xl font-bold text-orange-700">{formatCurrency(payroll.netPay)}</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PayrollViewModal;